import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { useAuthStore } from '../../stores/useAuthStore';
import { useInteractionStore } from '../../stores/useInteractionStore';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Spinner from '../common/Spinner';
import { motion } from 'framer-motion';
import { getMediaUrl, DEFAULT_AVATAR_URL, withImageFallback } from '../../utils/imageUtils';

const NotificationDropdown = ({ onClose }) => {
  const { socket } = useSocket();
  const currentUser = useAuthStore((state) => state.currentUser);
  const acceptFriendRequest = useInteractionStore((state) => state.acceptFriendRequest);
  const removeFriend = useInteractionStore((state) => state.removeFriend);
  const notifications = useInteractionStore((state) => state.notifications);
  const unreadCount = useInteractionStore((state) => state.unreadCount);
  const fetchNotifications = useInteractionStore((state) => state.fetchNotifications);
  const markOneAsRead = useInteractionStore((state) => state.markOneAsRead);
  const markAllAsRead = useInteractionStore((state) => state.markAllAsRead);
  const isNotificationsLoading = useInteractionStore((state) => state.isNotificationsLoading);
  const navigate = useNavigate();
  const adminRoles = ['admin', 'technician', 'admin_root', 'moderator', 'editor'];
  const isAdmin = currentUser?.roles?.some((role) => adminRoles.includes(role)) || adminRoles.includes(currentUser?.role);
  const messengerPath = isAdmin ? '/admin/messenger' : '/messenger';

  useEffect(() => {
    if (!currentUser?._id) return;
    fetchNotifications();
  }, [currentUser?._id, fetchNotifications]);

  useEffect(() => {
    if (!socket) return;

    const handleUpdateNotification = (updatedNotification) => {
      const state = useInteractionStore.getState();
      const filtered = (state.notifications || []).filter((notification) => notification._id !== updatedNotification._id);
      const nextNotifications = [updatedNotification, ...filtered];
      const nextUnreadCount = nextNotifications.filter((notification) => !notification.isRead).length;

      useInteractionStore.setState({
        notifications: nextNotifications,
        unreadCount: nextUnreadCount,
      });
    };

    socket.on('update-notification', handleUpdateNotification);

    return () => {
      socket.off('update-notification', handleUpdateNotification);
    };
  }, [socket]);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markOneAsRead(notification._id);
    }

    if (onClose) onClose();

    if (notification.link) {
      navigate(notification.link);
    } else if (notification.type === 'message' && notification.entityId) {
      navigate(messengerPath);
    }
  };

  const handleFriendRequest = async (event, action, notification) => {
    event.stopPropagation();
    try {
      if (action === 'accept') {
        await acceptFriendRequest(notification.sender._id);
      } else {
        await removeFriend(notification.sender._id);
      }
      markOneAsRead(notification._id);
    } catch (error) {
      console.error('Error handling friend request', error);
    }
  };

  const getGroupedNotifications = () => {
    const grouped = [];
    const map = new Map();

    (notifications || []).forEach((notification) => {
      if (notification.type === 'like' || notification.type === 'comment') {
        const key = `${notification.type}-${notification.entityId}`;
        if (map.has(key)) {
          const existing = map.get(key);
          existing.count = (existing.count || 1) + 1;
          existing.senders = existing.senders || [existing.sender];
          if (!existing.senders.find((sender) => sender?._id === notification.sender?._id)) {
            existing.senders.push(notification.sender);
          }
          existing.latestDate = notification.createdAt;
          existing.isRead = existing.isRead && notification.isRead;
        } else {
          const item = { ...notification, count: 1, senders: [notification.sender], latestDate: notification.createdAt };
          map.set(key, item);
          grouped.push(item);
        }
      } else {
        grouped.push(notification);
      }
    });

    return grouped.sort((a, b) => new Date(b.latestDate || b.createdAt) - new Date(a.latestDate || a.createdAt));
  };

  const groupedNotifications = getGroupedNotifications();

  const getNotificationMessage = (notification) => {
    if (notification.count > 1) {
      const othersCount = notification.count - 1;
      const action = notification.type === 'like' ? 'reaccionaron a' : 'comentaron en';
      return `${notification.sender?.username} y ${othersCount} personas más ${action} tu publicación.`;
    }
    return notification.message;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="fixed left-4 right-4 top-[70px] sm:absolute sm:top-full sm:right-0 sm:left-auto sm:w-96 mt-2 bg-white/90 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-white/20 dark:border-slate-700 z-50 ring-1 ring-black/5 dark:ring-white/10 overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-white/50 dark:bg-slate-900/70">
        <div className="flex justify-between items-center">
          <h3 className="text-md font-bold text-gray-800 dark:text-gray-100">Notificaciones</h3>
          <div className="flex items-center space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:underline font-semibold"
              >
                Marcar como leídas
              </button>
            )}
            <Link
              to={currentUser?.username ? `/profile/${currentUser.username}/edit` : '/settings'}
              state={{ initialTab: 'notifications' }}
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Configuración de notificaciones"
            >
              <i className="fas fa-cog"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto custom-scrollbar">
        {isNotificationsLoading ? (
          <div className="flex justify-center p-4"><Spinner size="sm" /></div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            <i className="far fa-bell-slash text-3xl text-gray-300 dark:text-gray-500 mb-2 block"></i>
            No tienes notificaciones nuevas.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-slate-700">
            {groupedNotifications.map((notification) => (
              <li
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`hover:bg-blue-50/50 dark:hover:bg-slate-800 transition-colors cursor-pointer ${!notification.isRead ? 'bg-blue-50/30 dark:bg-blue-900/20' : ''}`}
              >
                <div className="flex items-start px-4 py-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={getMediaUrl(notification.sender?.avatar, DEFAULT_AVATAR_URL)}
                      alt="Avatar"
                      className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                      onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
                    />
                    <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded-full p-0.5">
                      {notification.type === 'message' && <i className="fas fa-comment text-blue-500 text-xs"></i>}
                      {notification.type === 'friend_request' && <i className="fas fa-user-plus text-green-500 text-xs"></i>}
                      {notification.type === 'like' && <i className="fas fa-heart text-red-500 text-xs"></i>}
                      {notification.type === 'comment' && <i className="fas fa-comment-dots text-blue-400 text-xs"></i>}
                    </div>
                  </div>

                  <div className="ml-3 flex-1 min-w-0">
                    <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}`}>
                      {getNotificationMessage(notification)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {notification.latestDate || notification.createdAt
                        ? format(new Date(notification.latestDate || notification.createdAt), 'd MMM, HH:mm', { locale: es })
                        : 'Reciente'}
                    </p>

                    {notification.type === 'friend_request' && (
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={(event) => handleFriendRequest(event, 'accept', notification)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Aceptar
                        </button>
                        <button
                          onClick={(event) => handleFriendRequest(event, 'reject', notification)}
                          className="px-3 py-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 text-xs rounded-md hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                        >
                          Rechazar
                        </button>
                      </div>
                    )}
                  </div>

                  {!notification.isRead && (
                    <span className="h-2 w-2 bg-blue-500 rounded-full mt-2 flex-shrink-0 self-center"></span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-gray-50/80 dark:bg-slate-900/80 p-2 text-center border-t border-gray-100 dark:border-slate-700">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
        >
          Ver todas las notificaciones
        </Link>
      </div>
    </motion.div>
  );
};

export default NotificationDropdown;